/**
 * Appearance and behaviour settings.
 *
 * Everything on this page is stored in the browser, not on the server — see hooks/useSettings for
 * why. That is stated on the page rather than left to be discovered, because "I changed the colour
 * and my colleague didn't see it" is otherwise a reasonable thing to file as a bug.
 *
 * The page also carries a short "how this works" section. Staff will ask what the connection dot
 * means and why kick counts have a plus on them, and answering it here is cheaper than answering it
 * in Discord every time somebody new gets access.
 */

import { useState } from "react";
import { ACCENT_PRESETS, isValidHex, type Settings } from "../hooks/useSettings";
import type { Tier } from "../types";

interface Props {
  settings: Settings;
  update: (patch: Partial<Settings>) => void;
  reset: () => void;
  tier: Tier;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
}): JSX.Element {
  return (
    <div className="seg" role="group">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function SettingsTab({ settings, update, reset, tier }: Props): JSX.Element {
  const [customHex, setCustomHex] = useState(settings.accent);

  return (
    <div className="scroll">
      <div className="pad" style={{ maxWidth: 760 }}>
        <div className="card" style={{ marginBottom: 14 }}>
          <h2>Appearance</h2>

          <div className="setting">
            <div className="setting-info">
              <b>Theme</b>
              <p>System follows whatever your operating system is set to.</p>
            </div>
            <div className="setting-control">
              <Segmented
                value={settings.mode}
                onChange={(mode) => update({ mode })}
                options={[
                  { value: "dark", label: "Dark" },
                  { value: "light", label: "Light" },
                  { value: "system", label: "System" },
                ]}
              />
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Accent colour</b>
              <p>
                Drives links, active tabs, player names and the new-event highlight. Text on top of it
                flips between black and white automatically, so a pale colour stays readable.
              </p>
            </div>
            <div className="setting-control" style={{ flexDirection: "column", alignItems: "flex-end" }}>
              <div className="swatches">
                {ACCENT_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className="swatch"
                    style={{ background: preset.value }}
                    aria-pressed={settings.accent.toLowerCase() === preset.value.toLowerCase()}
                    aria-label={preset.name}
                    title={preset.name}
                    onClick={() => {
                      update({ accent: preset.value });
                      setCustomHex(preset.value);
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                <input
                  className="input"
                  style={{ width: "11ch", fontFamily: "var(--mono)" }}
                  value={customHex}
                  spellCheck={false}
                  aria-label="Custom accent colour, hex"
                  onChange={(event) => {
                    const next = event.target.value;
                    setCustomHex(next);
                    // Applied live, but only once it is actually a colour — otherwise the whole page
                    // flickers through nonsense while somebody types the six characters.
                    if (isValidHex(next)) update({ accent: next.startsWith("#") ? next : `#${next}` });
                  }}
                />
                <input
                  type="color"
                  className="input"
                  style={{ width: 40, padding: 2, height: 32 }}
                  value={isValidHex(customHex) ? (customHex.startsWith("#") ? customHex : `#${customHex}`) : "#cf91ff"}
                  aria-label="Pick an accent colour"
                  onChange={(event) => {
                    setCustomHex(event.target.value);
                    update({ accent: event.target.value });
                  }}
                />
              </div>
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Row density</b>
              <p>How tall each line in the log list is. Compact fits noticeably more on screen.</p>
            </div>
            <div className="setting-control">
              <Segmented
                value={settings.density}
                onChange={(density) => update({ density })}
                options={[
                  { value: "compact", label: "Compact" },
                  { value: "cosy", label: "Cosy" },
                  { value: "roomy", label: "Roomy" },
                ]}
              />
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Server column</b>
              <p>Hide it to give messages more room if you only run one region.</p>
            </div>
            <div className="setting-control">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.showServer}
                  onChange={(event) => update({ showServer: event.target.checked })}
                />
                Show
              </label>
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Highlight new events</b>
              <p>
                Briefly flashes rows as they arrive. Ignored anyway if your system is set to reduce
                motion.
              </p>
            </div>
            <div className="setting-control">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={settings.flashNew}
                  onChange={(event) => update({ flashNew: event.target.checked })}
                />
                Flash
              </label>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 14 }}>
          <h2>Time</h2>
          <div className="setting">
            <div className="setting-info">
              <b>Timestamps</b>
              <p>
                UTC is the default because the in-game <code>/debug</code> command prints UTC. If you
                switch to local time, the same event will show a different clock here than it does in
                game — worth knowing before you compare the two while working something out.
              </p>
            </div>
            <div className="setting-control">
              <Segmented
                value={settings.timeMode}
                onChange={(timeMode) => update({ timeMode })}
                options={[
                  { value: "utc", label: "UTC" },
                  { value: "local", label: "Local" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 14 }}>
          <h2>How this works</h2>

          <div className="setting">
            <div className="setting-info">
              <b>Where settings are stored</b>
              <p>
                In this browser only. Nothing on this page is shared with other staff, and it is not
                saved to your account — clearing site data or using a different device starts from the
                defaults again.
              </p>
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>The connection dot</b>
              <p>
                <b style={{ color: "var(--ok)" }}>Live</b> means events arrive within about a second of
                happening in game. <b style={{ color: "var(--warn)" }}>Degraded</b> means Redis is
                unreachable and the page is reading the database every three seconds instead — still
                working, just slower. It never shows stale data as if it were current.
              </p>
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Why some kick counts have a “+”</b>
              <p>
                The game only ever stored the most recent kick per player, so older ones are gone. The
                new <code>kick_log</code> table records them properly from the day it was added
                onwards; until then the number shown is a floor, not a total.
              </p>
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Your access level</b>
              <p>
                You are <b>{tier}</b>. It is set on your account by an admin — ask one if it needs
                changing.
                {tier === "viewer" && " Viewers can see the debug logs and punishment history, but not voice logs."}
                {tier === "staff" &&
                  " Staff can see voice logs and use the staff portal: LOA, punishment lookup and evidence."}
                {tier === "admin" && " Admins can see everything, manage accounts and read the access audit log."}
              </p>
            </div>
          </div>

          <div className="setting">
            <div className="setting-info">
              <b>Keyboard shortcuts</b>
              <p>
                <code>/</code> focuses search · <code>j</code> and <code>k</code> move through rows ·{" "}
                <code>Esc</code> closes the player panel.
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Reset</h2>
          <div className="setting">
            <div className="setting-info">
              <b>Restore defaults</b>
              <p>Dark theme, amethyst accent, cosy rows, UTC timestamps.</p>
            </div>
            <div className="setting-control">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  reset();
                  setCustomHex("#cf91ff");
                }}
              >
                Reset appearance
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
